// import dependencies
const fs = require('fs');
const {exec} = require('child_process');

// constants
const EXIT_DELAY = 30; // wait X seconds to exit the process when the queue is empty
const GENERATION_INTERVAL_SECONDS = 5; // every # seconds try to generate thumbnail from queue
const MAX_RETRY_MINS = 3; // max # of mins worth of seconds to retry for per clip
const MAX_GENERATION_ATTEMPTS = ((MAX_RETRY_MINS * 60) / GENERATION_INTERVAL_SECONDS); // only try for X minutes worth of attempts based on the interval

// local variables
let thumbnailQueue = [];
let retryQueue = [];
let thumbnailQueueConsumer = null;
let head = null;
let ffmpegPath = null;
let exitTimeout = null;

/**
 * Return whether or not a file exists.
 * @param file
 * @returns {boolean}
 */
const fileExists = (file) => {
  // if the path doesn't exist, create it
  try {
    // access the file
    fs.accessSync(file);
    return true;
  } catch (e) {
    // does not exist
    return false;
  }
};

/**
 * Logging function. Make sure to ensure that the `log` object is serializable for IPC.
 */
const log = async (level = 'info', log) => {
  // emit to parent
  await emit({
    action : 'log',
    level : level,
    message : log
  });
};

/**
 * Receive IPC message events.
 */
process.on('message', async (msg) => {
  // action
  const action = msg.action || '';

  // check for incoming msg actions
  switch (action) {
    case 'init': {
      thumbnailQueue = [];
      retryQueue = [];
      ffmpegPath = msg.ffmpegPath;

      // check ffmpeg path
      if (!ffmpegPath || (!fileExists(ffmpegPath) && process.platform === 'win32')) {
        // log
        await log('error', `Invalid ffmpeg path provided upon initialization:\n${ffmpegPath}`);
        return;
      }

      // log
      log('info', `Thumbnail generation process initialized!`);

      // end
      break;
    }

    case 'queue': {
      // check if exiting
      if (exitTimeout) {
        // log
        log('info', `Interrupting exit! Something else is coming into the queue...`);
        clearTimeout(exitTimeout);
        exitTimeout = null;
      }

      // the clip metadata object
      const clip = msg.clip;

      // validate it
      if (!clip || !clip.uuid || !clip.videoPath || !clip.thumbnailPath) {
        // log
        await log('error', `Invalid or missing clip metadata provided:\n${JSON.stringify(clip)}`);
        return;
      }

      // log
      log('info', `Queuing clip for thumbnail generation:\n${JSON.stringify(clip)}`);

      // queue the clip for thumbnail generation
      await queue(clip);

      // end
      break;
    }

    case 'cancel': {
      // the clip metadata  object
      const clip = msg.clip;

      // validate it
      if (!clip || !clip.uuid) {
        // log
        await log('error', `Invalid or missing clip metadata provided:\n${JSON.stringify(clip)}`);
        return;
      }

      // queue the clip for thumbnail generation
      await cancel(clip);

      // end
      break;
    }

    default: {
      log('error', `Unsupported action received: ${action}`);
      break;
    }
  }
});

/**
 * Emit the message back to the parent thread.
 * @param msg
 */
const emit = async (msg) => {
  try {
    // emit the event back to the main thread
    process.send(msg);
  } catch (err) {
    console.error(err)
  }
};

/**
 * Queue the clip thumbnail for generation.
 * @param clip
 * @returns {Promise<void>}
 */
const queue = async (clip) => {
  // add the thumbnail to queue
  thumbnailQueue.push({
    clip : clip,
    attempts : 0,
    requeues : 0,
    retries : 0
  });

  // check if we need to start the queue consumer
  if (!thumbnailQueueConsumer) {
    // log
    log('info', `Beginning polling for thumbnail queue...`);

    // start the queue consumer back up
    startThumbnailPolling();
  }
};

/**
 * Cancel thumbnail.
 */
const cancel = async (clip) => {
  try {
    // check if is the pending thumbnail
    if (head && head.clip.uuid === clip.uuid) {
      head.cancel = true;

      // log
      log('info', `Set pending thumbnail to cancelled for clip: ${clip.uuid}`);
    }

    // check if thumbnail is being generated
    if (thumbnailQueue.length > 0) {
      for (let i = 0; i < thumbnailQueue.length; i++) {
        if (thumbnailQueue[i].clip.uuid === clip.uuid) {
          // set to canceled
          thumbnailQueue[i].cancel = true;

          // remove from queue
          thumbnailQueue = thumbnailQueue.slice(i, 1);

          // log
          log('info', `Thumbnail generation cancelled for clip: ${clip.uuid}`);
          break;
        }
      }
    }

    // check if thumbnail is being generated in retry queue
    if (retryQueue.length > 0) {
      for (let i = 0; i < retryQueue.length; i++) {
        if (retryQueue[i].clip.uuid === clip.uuid) {
          // set to  canceled
          retryQueue[i].cancel = true;

          // remove from queue
          retryQueue = retryQueue.slice(i, 1);

          // log
          log('info', `Thumbnail generation cancelled for clip: ${clip.uuid}`);
          break;
        }
      }
    }
  } catch (e) {
    // log
    log('error', e.toString());
  }
};

/**
 * Poll the queue. Retrieves and removes the item at the front of the queue.
 */
const poll = () => {
  // check the queue
  if (thumbnailQueue.length > 0) {
    // get + remove the item at the front of the primary queue
    return thumbnailQueue.shift();
  } else if (retryQueue.length > 0) {
    // get + remove the item at the front of the retry queue
    return retryQueue.shift();
  }

  // no items in queue, return null
  return null;
};

/**
 * Skip this clip.
 * @param clip
 * @returns {Promise<void>}
 */
const skip = async (clip, reason) => {
  // log
  log('warn', `Skipping thumbnail generation for clip: ${clip.uuid}`);

  // exists
  const exists = fileExists(clip.videoPath);

  // emit completion event
  await emit({
    action : `thumbnail.skipped`,
    clip : clip,
    error : exists ? 'Video file is corrupt or has not finished encoding' : 'Video file does not exist',
    exists : exists
  });
};

/**
 * Thumbnail queue consumer function. It is called from setTimeout, at various intervals.
 */
async function queueConsumerFn () {
  let nextTimeout = GENERATION_INTERVAL_SECONDS * 1000;
  try {
    // check if we are generating a thumbnail
    if (head) {
      // return and wait for the next cycle
      thumbnailQueueConsumer = setTimeout(queueConsumerFn, nextTimeout);
      return
    }

    // poll the queue for the item in front
    head = poll();

    // check if the item exists
    if (head) {
      // check the number of attempts or if the clip is being cancelled
      if (head.retries >= MAX_GENERATION_ATTEMPTS || head.cancel) {
        // don't even re-insert into the queue
        await skip(head.clip);
        head = null;
        thumbnailQueueConsumer = setTimeout(queueConsumerFn, nextTimeout);
        return
      }

      // do the things
      try {
        // try to generate the thumbnail
        if (await generate(head.clip)) {
          // emit completion event
          await emit({
            action : `thumbnail.ready`,
            clip : head.clip
          });
        }
      } catch (e) {
        // increment retry count
        head.retries++;

        // Use a smaller timeout for the initial few iterations, so that thumbnail can be generated quickly for smaller clips
        nextTimeout = Math.min(nextTimeout, head.retries * 1000);

        let time48HoursAgo = new Date(new Date().getTime() - (48 * 60 * 60 * 1000));

        // if clip created more than 48 hours ago and cannot generate thumbnails, don't retry
        if (head.clip.timeCreated <= time48HoursAgo) {
          log('info', `Clip created more than 48 hours ago and cannot generate thumbnail. Won't retry thumbnail generation.`);
          cancel(head.clip)
        }

        // video file wasn't ready or something, add to retry queue
        retryQueue.push(head);
      }

      // set current clip as null
      head = null;
    } else {
      if (!exitTimeout) {
        // log
        log('info', `Queue is empty, waiting ${EXIT_DELAY}s before exiting...`);

        // wait 10s to exit the generation process
        exitTimeout = setTimeout(async () => {
          // log
          await log('info', `Exiting...`);

          // there are no pending thumbnails, and the queue is empty. we can shut down the queue consumer.
          clearInterval(thumbnailQueueConsumer);
          thumbnailQueueConsumer = null;

          // exit the thumbnail process until it's needed again
          process.exit();
        }, EXIT_DELAY * 1000);
      }
    }
  } catch (e) {
    // thumbnail skipped
    await log('warn', e.toString());

    // thumbnail skipped
    await log('error', `Error processing thumbnail queue`);
  }
  thumbnailQueueConsumer = setTimeout(queueConsumerFn, nextTimeout);
}

/**
 * Start the thumbnail queue consumer.
 */
const startThumbnailPolling = async () => {
  // check if queue consumer exists
  if (thumbnailQueueConsumer) {
    // clear the timeout
    clearTimeout(thumbnailQueueConsumer);
  }

  // start new consumer
  thumbnailQueueConsumer = setTimeout(queueConsumerFn, 100);
};

/**
 * Generate the clip thumbnail.
 * Throws an error if it fails (for example if the video has not fully encoded yet).
 * @param clip
 * @returns {Promise<*>}
 */
const generate = async (clip) => {
  // create image
  return new Promise((resolve, reject) => {
    // check if file already exists for some reason
    if (fileExists(clip.thumbnailPath)) {
      resolve(true);
      return;
    }

    // the ffmpeg command to execute
    const ffmpegCommand = `"${process.platform === 'win32' ? ffmpegPath : 'ffmpeg'}" -i "${clip.videoPath}" -frames:v 1 -vf "select=eq(n\\,0)" -q:v 3 "${clip.thumbnailPath}"`;

    // log cmd
    log('debug', `Executing generation command:\n\n${ffmpegCommand}\n`);

    // exec
    exec(ffmpegCommand, {maxBuffer: 1024*1024*100}, async (err) => {
      // check error
      if (err) {
        // check for expected error
        if (err.toString().indexOf('Invalid data found when processing input')) {
          // check if video exists
          if (fileExists(clip.videoPath)) {
            log('warn', `Could not generate thumbnail, video is not finished encoding: ${clip.uuid}, ${clip.videoPath}`);
          } else {
            log('warn', `Could not generate thumbnail, video does not exist: ${clip.uuid}, ${clip.videoPath}`);
          }
        } else {
          log('error', `Unexpected error generating thumbnail:\n\t${err.toString()}`);
        }

        // return error
        return reject(err);
      }

      // log
      log('info', `Thumbnail generated for clip: ${clip.uuid}`);

      // resolve
      resolve(true);
    });
  });
};
